import React, { useState, useEffect } from 'react';
import { Settings2 } from 'lucide-react';
import { FIELD_TYPES, STRUCTURAL_TYPES, TYPE_ICONS, CONTACT_MAPPINGS, INPUT, LABEL } from '../../lib/formConstants';
import VisibilityEditor from './VisibilityEditor';

/**
 * FieldInspector — standalone field/element settings panel.
 *
 * Feature-parity replacement for the inline `FieldSettingsForm` that lived
 * inside FormBuilder.jsx.  Uses the same partial-merge callback contract
 * (`onUpdate({ key: value })`) and the same `map_to` property name so it can
 * be dropped in as a 1:1 swap.
 *
 * Props:
 *   field         — the selected field object (or null)
 *   onUpdate      — callback receiving a partial updates object
 *   customFields  — array of custom CRM field definitions [{ key, label, field_type }]
 *   allFields     — flattened array of all form fields (for conditional-visibility dropdown)
 *   forwarders    — form forwarders (late-source warning in DeriveEditor)
 *   steps         — form steps (late-source warning in DeriveEditor)
 */
export default function FieldInspector({ field, onUpdate, customFields = [], allFields = [], forwarders = [], steps = [] }) {
    // Local draft state for the spacer height input — allows the user to
    // freely clear and type without the value snapping to the clamp range
    // on every keystroke. The clamped value is committed on blur.
    const [spacerDraft, setSpacerDraft] = useState(String(field?.spacer_height ?? 32));

    // Sync the draft when the field changes (e.g., selecting a different spacer)
    useEffect(() => {
        setSpacerDraft(String(field?.spacer_height ?? 32));
    }, [field?.field_id, field?.spacer_height]);

    if (!field) {
        return (
            <div className="text-center py-12 text-stone-400">
                <Settings2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Select an element to edit its settings</p>
            </div>
        );
    }

    const isStructural = STRUCTURAL_TYPES.includes(field.field_type);
    const isContainer = field.field_type === 'container';
    const isHtmlBlock = field.field_type === 'html_block';

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-stone-100">
                <span className="text-lg">{TYPE_ICONS[field.field_type] || '❓'}</span>
                <span className="text-sm font-semibold text-stone-700">
                    {isContainer ? 'Container Settings' : isStructural ? 'Structural Element' : 'Field Settings'}
                </span>
            </div>

            {/* Label */}
            <div>
                <label className={LABEL}>Label</label>
                <input value={field.label || ''} onChange={e => onUpdate({ label: e.target.value })}
                    className={INPUT} placeholder={isContainer ? 'Container label (optional)...' : 'Field label...'} />
            </div>

            {/* Field type selector */}
            <div>
                <label className={LABEL}>Element Type</label>
                <select value={field.field_type} onChange={e => onUpdate({ field_type: e.target.value })} className={INPUT}>
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                </select>
            </div>

            {/* Container-specific settings */}
            {isContainer && (
                <>
                    <div>
                        <label className={LABEL}>Columns (1-12)</label>
                        <input type="number" min={1} max={12} value={field.columns || 1}
                            onChange={e => onUpdate({ columns: Math.max(1, Math.min(12, parseInt(e.target.value) || 1)) })}
                            className={INPUT} />
                        <p className="text-[10px] text-stone-400 mt-1">Each child occupies one column. Collapses to 1 column on mobile.</p>
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-xs text-stone-600">
                            <input type="checkbox" checked={field.collapsible || false}
                                onChange={e => onUpdate({ collapsible: e.target.checked })}
                                className="rounded border-stone-300" />
                            Collapsible (expand/collapse on public form)
                        </label>
                    </div>
                </>
            )}

            {/* Spacer height setting — uses local draft state so the user can
                freely clear and type. Clamping to 8-200 happens on blur. */}
            {field.field_type === 'spacer' && (
                <div>
                    <label className={LABEL}>Height (px)</label>
                    <input type="number" min={8} max={200}
                        value={spacerDraft}
                        onChange={e => setSpacerDraft(e.target.value)}
                        onBlur={() => {
                            const parsed = parseInt(spacerDraft, 10);
                            const clamped = isNaN(parsed) || parsed < 8 ? 8 : Math.min(200, parsed);
                            setSpacerDraft(String(clamped));
                            onUpdate({ spacer_height: clamped });
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        className={INPUT} />
                    <p className="text-[10px] text-stone-400 mt-1">Vertical spacing in pixels (8-200).</p>
                </div>
            )}

            {/* Margin overrides — spacers ARE spacing, so they don't get margins */}
            {field.field_type !== 'spacer' && (
                <div>
                    <label className={LABEL}>Margin (px)</label>
                    <div className="grid grid-cols-4 gap-1.5">
                        {['top', 'right', 'bottom', 'left'].map(side => (
                            <div key={side}>
                                <input type="number" value={field.margin?.[side] ?? ''}
                                    onChange={e => onUpdate({
                                        margin: {
                                            ...(field.margin || {}),
                                            [side]: e.target.value === '' ? null : parseInt(e.target.value, 10) || 0,
                                        },
                                    })}
                                    className="w-full px-1.5 py-1.5 text-xs text-center border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    placeholder="–" />
                                <p className="text-[9px] text-stone-400 text-center mt-0.5 capitalize">{side}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* HTML block content */}
            {isHtmlBlock && (
                <div>
                    <label className={LABEL}>HTML Content</label>
                    <textarea value={field.html_content || ''} onChange={e => onUpdate({ html_content: e.target.value })}
                        rows={4} className={INPUT} placeholder="<p>Custom HTML content...</p>" />
                </div>
            )}

            {/* Non-structural, non-container field settings */}
            {!isStructural && !isContainer && (
                <>
                    <div>
                        <label className="flex items-center gap-2 text-xs text-stone-600">
                            <input type="checkbox" disabled={!!field.hidden}
                                checked={!field.hidden && (field.required || false)}
                                onChange={e => onUpdate({ required: e.target.checked })}
                                className="rounded border-stone-300 disabled:opacity-40" />
                            Required field
                            {field.hidden && (
                                <span className="text-[10px] text-stone-400">
                                    — not available on hidden fields
                                </span>
                            )}
                        </label>
                    </div>
                    <div>
                        <label className={LABEL}>Placeholder</label>
                        <input value={field.placeholder || ''} onChange={e => onUpdate({ placeholder: e.target.value })}
                            className={INPUT} placeholder="Placeholder text..." />
                    </div>
                    <div>
                        <label className={LABEL}>Help Text</label>
                        <input value={field.help_text || ''} onChange={e => onUpdate({ help_text: e.target.value })}
                            className={INPUT} placeholder="Helper text shown below the field..." />
                    </div>
                    <div>
                        <label className={LABEL}>Default Value</label>
                        <input value={field.default_value || ''} onChange={e => onUpdate({ default_value: e.target.value })}
                            className={INPUT} placeholder="Pre-filled value..." />
                    </div>
                    <div>
                        <label className={LABEL}>Map to CRM Field</label>
                        <select value={field.map_to || ''} onChange={e => onUpdate({ map_to: e.target.value || null })} className={INPUT}>
                            <option value="">No mapping</option>
                            <optgroup label="Contact Fields">
                                {CONTACT_MAPPINGS.filter(m => m.group === 'Contact').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Company Fields">
                                {CONTACT_MAPPINGS.filter(m => m.group === 'Company').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </optgroup>
                            {customFields.length > 0 && (
                                <optgroup label="Custom Fields">
                                    {customFields.map(cf => (
                                        <option key={cf.key} value={cf.key}>{cf.label} ({cf.field_type})</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                        {field.map_to && CONTACT_MAPPINGS.find(m => m.value === field.map_to)?.group === 'Company' && (
                            <p className="text-[10px] text-stone-400 mt-1">
                                Company mappings upsert an account on submission. Requires a <code className="bg-stone-100 px-1 rounded">Company Name</code> field too.
                            </p>
                        )}
                    </div>

                    {/* Options for select/multi_select — stores raw lines
                        including trailing empty strings so the user can press
                        Enter to add options. Empty strings are filtered out
                        at render time in the public form and preview. */}
                    {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                        <div>
                            <label className={LABEL}>Options (one per line)</label>
                            <textarea value={(field.options || []).join('\n')}
                                onChange={e => onUpdate({ options: e.target.value.split('\n') })}
                                rows={3} className={INPUT} placeholder="Option 1&#10;Option 2&#10;Option 3" />
                        </div>
                    )}

                    {/* Max Selections — multi_select only */}
                    {field.field_type === 'multi_select' && (
                        <div>
                            <label className={LABEL}>Max Selections</label>
                            <input type="number" min={1} value={field.config?.max_selections ?? ''}
                                onChange={e => onUpdate({
                                    config: {
                                        ...field.config,
                                        max_selections: e.target.value ? parseInt(e.target.value) : null,
                                    },
                                })}
                                className={INPUT} placeholder="No limit" />
                            <p className="text-[10px] text-stone-400 mt-1">Maximum number of items a user can select. Leave blank for no limit.</p>
                        </div>
                    )}

                    {/* Mirror Values From — select (dropdown) only */}
                    {field.field_type === 'select' && (
                        <div>
                            <label className={LABEL}>Mirror Values From</label>
                            <select
                                value={field.config?.mirror_source_field_id || ''}
                                onChange={e => onUpdate({
                                    config: {
                                        ...field.config,
                                        mirror_source_field_id: e.target.value || null,
                                    },
                                })}
                                className={INPUT}
                            >
                                <option value="">No mirroring (use options above)</option>
                                {allFields
                                    .filter(f => f.field_type === 'multi_select' && f.field_id !== field.field_id)
                                    .map(f => (
                                        <option key={f.field_id} value={f.field_id}>
                                            {f.label || 'Untitled'} ({f.field_type})
                                        </option>
                                    ))}
                            </select>
                            <p className="text-[10px] text-stone-400 mt-1">Only show the items selected in the source multi-select field as dropdown options. The source field must appear before this dropdown in the form flow.</p>
                        </div>
                    )}

                    {/* Visibility — Always visible / Show when… / Always hidden.
                        VisibilityEditor owns ShowWhenEditor and DeriveEditor. */}
                    <VisibilityEditor
                        field={field}
                        allFields={allFields}
                        forwarders={forwarders}
                        steps={steps}
                        onUpdate={onUpdate}
                    />

                    {/* Repeatable */}
                    <div>
                        <label className="flex items-center gap-2 text-xs text-stone-600">
                            <input type="checkbox" checked={field.repeatable || false}
                                onChange={e => onUpdate({ repeatable: e.target.checked })}
                                className="rounded border-stone-300" />
                            Repeatable (allow multiple instances)
                        </label>
                        {field.repeatable && (
                            <div className="mt-2 space-y-2 pl-6 border-l-2 border-stone-100">
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <label className={LABEL}>Min Instances</label>
                                        <input type="number" min={1} value={field.repeatable_min ?? 1}
                                            onChange={e => onUpdate({ repeatable_min: parseInt(e.target.value) || 1 })} className={INPUT} />
                                    </div>
                                    <div className="flex-1">
                                        <label className={LABEL}>Max (blank = ∞)</label>
                                        <input type="number" min={1} value={field.repeatable_max ?? ''}
                                            onChange={e => onUpdate({ repeatable_max: e.target.value ? parseInt(e.target.value) : null })} className={INPUT} placeholder="∞" />
                                    </div>
                                </div>
                                <div>
                                    <label className={LABEL}>Add Button Label</label>
                                    <input value={field.repeatable_label || 'Add another'}
                                        onChange={e => onUpdate({ repeatable_label: e.target.value })} className={INPUT} />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
